import { MISSING_VALUE, NO_CONFIDENCE_LEVEL, NO_LIFT } from '@constants/stats';

export type ValueInput = number | null | undefined;

type FormatterOptions = Partial<{
  full: boolean;
  decimals: number;
  isPercentage: boolean;
  floor: number;
}>;

type OptionsSubset<T extends keyof FormatterOptions> = Pick<
  FormatterOptions,
  T
>;

type LiftOptions = Partial<{
  noLiftValue: string;
  decimals: number;
}>;

const roundIfNumber = (value: ValueInput) =>
  typeof value === 'number' ? Math.round(value) : value;

export const formatters = {
  lift: (
    value: ValueInput,
    options?: OptionsSubset<'isPercentage'> & LiftOptions
  ) => {
    return fSimpleLift(
      value && options?.isPercentage ? value / 100 : value,
      options
    );
  },
  visits: (value: ValueInput, options?: OptionsSubset<'full' | 'floor'>) =>
    fVisits(
      roundIfNumber(value) ?? null,
      options?.full ? 'standard' : 'compact'
    ),
  locations: (value: ValueInput) =>
    fNumber(value, { notation: 'standard', useGrouping: false }),
  impressions: (value: ValueInput, options?: OptionsSubset<'full'>) =>
    fNumber(roundIfNumber(value), {
      notation: options?.full ? 'standard' : 'compact',
      maxDecimals: 1,
    }),
  users: (value: ValueInput, options?: OptionsSubset<'full'>) =>
    fNumber(roundIfNumber(value), {
      notation: options?.full ? 'standard' : 'compact',
      maxDecimals: 1,
    }),
  confidenceLevel: (
    value: ValueInput,
    lift: ValueInput,
    options?: OptionsSubset<'isPercentage'>
  ) =>
    fConfidenceLevel(
      value && options?.isPercentage ? value / 100 : value,
      lift
    ),
  currency: (value: ValueInput, options?: OptionsSubset<'full' | 'decimals'>) =>
    fCurrency(value ?? null, undefined, undefined, {
      notation: options?.full ? 'standard' : 'compact',
      ...(options?.decimals !== undefined && {
        maximumFractionDigits: options.decimals,
        minimumFractionDigits: options.decimals,
      }),
    }),
  percentage: (value: ValueInput, options?: OptionsSubset<'decimals'>) =>
    fSimplePercentage(value, {
      maximumFractionDigits: options?.decimals ?? 0,
      minimumFractionDigits: options?.decimals ?? 0,
    }),
  number: (
    value: ValueInput,
    options?: OptionsSubset<'full' | 'decimals' | 'floor'>
  ) =>
    fNumber(value, {
      maxDecimals: options?.decimals,
      minDecimals: options?.decimals,
      notation: options?.full ? 'standard' : 'compact',
      floor: options?.floor,
    }),
};

const fSimpleLift = (
  value: ValueInput,
  options: Intl.NumberFormatOptions & LiftOptions = {}
) => {
  const { noLiftValue, ...formatOptions } = options;

  if (_.isNil(value)) {
    return MISSING_VALUE;
  } else if (value <= 0) {
    return noLiftValue ?? NO_LIFT;
  }

  return fSimplePercentage(value, formatOptions);
};

const fVisits = (
  value: number | null,
  notation: FormatNumberOptions['notation']
) => {
  if (_.isNil(value)) {
    return MISSING_VALUE;
  }
  if (value <= 0) {
    return '0';
  }
  return fNumber(value, { maxDecimals: 1, minDecimals: 0, notation });
};

const fCurrency = (
  value: number | string | null,
  numDecimal = 1,
  overrideCutoff = false,
  options: Intl.NumberFormatOptions = {}
) => {
  if (value === null) return `$${MISSING_VALUE}`;
  // For numbers less than $1000, we want to display the full value including cents, e.g. $5.49, including the .49
  // For round numbers, like $5.00 or $0.00, we will omit the .00 and just display $5 or $0, for cleaner display
  const twoDecimalCutoff = 1000;
  const valueAsFloat = Number.parseFloat(value.toString());
  const targetDecimal = valueAsFloat % 1 === 0 ? 0 : 2;

  const adjustedNumDecimal =
    +value < twoDecimalCutoff && !overrideCutoff ? targetDecimal : numDecimal;

  return Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: adjustedNumDecimal,
    minimumFractionDigits: adjustedNumDecimal,
    ...options,
  }).format(valueAsFloat);
};

const fConfidenceLevel = (value: ValueInput, lift: ValueInput) => {
  if (!lift || lift <= 0) {
    return fSimplePercentage(0, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
  if (_.isNil(value)) {
    return MISSING_VALUE;
  }
  if (value <= 0) {
    return NO_CONFIDENCE_LEVEL;
  }
  return fSimplePercentage(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const fSimplePercentage = (
  value: ValueInput,
  options: Intl.NumberFormatOptions = {}
) => {
  if (_.isNil(value)) return `${MISSING_VALUE}%`;

  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    ...options,
  }).format(value);
};

type FormatNumberOptions = Partial<{
  maxDecimals: number;
  minDecimals: number;
  notation: Intl.NumberFormatOptions['notation'];
  floor: number;
  useGrouping: Intl.NumberFormatOptions['useGrouping'];
}>;

const fNumber = (value: ValueInput, options: FormatNumberOptions) => {
  if (value === null || value === undefined) {
    return MISSING_VALUE;
  }

  const valueMetadata: { value: number; prefix: string } =
    typeof options.floor === 'number' && value < options.floor
      ? { value: options.floor, prefix: '<' }
      : { value, prefix: '' };

  return `${valueMetadata.prefix}${Intl.NumberFormat('en-US', {
    notation: options.notation ?? 'compact',
    maximumFractionDigits: options.maxDecimals,
    minimumFractionDigits: options.minDecimals,
    useGrouping: options.useGrouping ?? true,
  }).format(Number.parseFloat(valueMetadata.value.toString()))}`;
};
